/** @fileoverview Shared look of the buttons an app shows, so screen, header and form buttons match. */

import type { FabrikButtonStyle, FabrikComponentSize } from '../../types/apps';

/** What a caller picks for an app button: its look and its size step. */
export type AppButtonInput = {
  variant?: FabrikButtonStyle['variant'];
  size?: FabrikComponentSize;
};

type AppButtonLook = {
  variant: 'default' | 'outline' | 'ghost' | 'destructive';
  size: 'default';
  className: string;
};

/* The shared button variant and extra classes of each app button look. */
const VARIANT_LOOKS: Record<
  FabrikButtonStyle['variant'],
  { variant: AppButtonLook['variant']; className: string }
> = {
  primary: { variant: 'default', className: 'shadow-xs' },
  /* A white fill and a border, so it reads as a button on any page colour. */
  secondary: { variant: 'outline', className: 'bg-card shadow-xs dark:bg-card' },
  ghost: { variant: 'ghost', className: '' },
  danger: { variant: 'destructive', className: '' },
};

/* Height, side padding and text of each size step; md is the usual button. */
const SIZE_CLASSES: Record<FabrikComponentSize, string> = {
  sm: 'h-app-control-compact px-3 type-body',
  md: 'h-app-control px-3.5 type-body',
  lg: 'h-app-control-large px-4 type-body',
  xl: 'h-app-row px-5 type-heading',
};

/**
 * Picks the variant, size and extra classes for an app button. The height and text follow the
 * app's size preset, so a compact app has smaller buttons.
 * @param look the button look (secondary when left out) and its size step (md when left out)
 */
export const appButtonLook = ({
  variant = 'secondary',
  size = 'md',
}: AppButtonInput): AppButtonLook => {
  const { variant: buttonVariant, className } = VARIANT_LOOKS[variant];
  return {
    variant: buttonVariant,
    /* Size stays "default"; the height and text come from the app steps. */
    size: 'default',
    className: [SIZE_CLASSES[size], 'rounded-md', className].filter(Boolean).join(' '),
  };
};
